import type {
  Ampli,
  BrowserClient,
  PromiseResult,
} from '~/domain/plugins/services/ampli';
import { clientLogger } from '~/lib/logger';

/**
 * The generated wrapper as call sites see it. Only `amplitude.client` loads
 * Amplitude, and `client` admits it is absent until then.
 */
export type Analytics = Omit<Ampli, 'load' | 'client'> & {
  readonly client: BrowserClient | undefined;
};

type HeldCall = {
  method: string;
  args: unknown[];
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
};

// Far more than a visitor triggers while the SDK downloads. The cap only bites
// when it never arrives (no API key, a failed chunk), and bounds what we keep.
const MAX_HELD_CALLS = 100;

// Read by code that inspects any object (promise checks, serialisers, React,
// test matchers), so not tracking calls: holding them replays bogus calls.
const NOT_CALLS = new Set(['then', 'toJSON', '$$typeof', 'asymmetricMatch']);

let loaded: Ampli | undefined;
let held: HeldCall[] = [];

function invoke(instance: Ampli, method: string, args: unknown[]) {
  const fn = Reflect.get(instance, method) as (
    ...args: unknown[]
  ) => PromiseResult<unknown>;
  return fn.apply(instance, args);
}

/**
 * Hands over the loaded instance and replays the held calls, in order. Called
 * after enrichment and identify, so replayed events match live ones.
 */
export function attachAmpli(instance: Ampli): void {
  loaded = instance;
  const calls = held;
  held = [];
  for (const { method, args, resolve, reject } of calls) {
    try {
      resolve(invoke(instance, method, args).promise);
    } catch (error) {
      clientLogger.warn(error, `[amplitude] held ${method} call failed`);
      reject(error);
    }
  }
}

/**
 * Stands in for the generated `ampli`, whose SDK import is too heavy for the
 * JavaScript a page loads before its first paint. Ampli drops calls made
 * before it has loaded; this holds them, so loading later loses no events.
 */
export const ampli = new Proxy({} as Analytics, {
  get(target, property) {
    if (property === 'isLoaded') return loaded !== undefined;
    if (property === 'client') return loaded?.client;
    if (
      typeof property !== 'string' ||
      property in target ||
      NOT_CALLS.has(property)
    ) {
      return Reflect.get(target, property);
    }

    return (...args: unknown[]) => {
      if (loaded) return invoke(loaded, property, args);
      if (held.length >= MAX_HELD_CALLS) return { promise: Promise.resolve() };
      const promise = new Promise((resolve, reject) => {
        held.push({ method: property, args, resolve, reject });
      });
      // Most call sites ignore the result; a failed replay is logged instead.
      promise.catch(() => undefined);
      return { promise };
    };
  },
});
